/**
 * Synchronization utilities for real-time collaboration
 *
 * Provides shape change detection, serialization/deserialization,
 * and diff calculation for efficient sync.
 */

import { Shape } from '../types/Shape';
import { ShapeData, UpdateAction } from '../types/Collaboration';

/**
 * Shape change type
 */
export type ShapeChangeType = 'add' | 'update' | 'delete';

/**
 * Single shape change
 */
export interface ShapeChange {
  type: ShapeChangeType;
  shapeId: string;
  shape?: Shape;
  previousShape?: Shape;
}

/**
 * Diff result containing all changes
 */
export interface ShapeDiff {
  added: Shape[];
  updated: Shape[];
  deleted: string[];
  hasChanges: boolean;
}

/**
 * Fields that should be excluded from sync (client-only fields)
 */
const EXCLUDED_FIELDS: (keyof Shape)[] = [
  'pdfFile',        // File objects cannot be serialized
  'pageImages',     // HTMLImageElement cannot be serialized
];

/**
 * Fields that contain large data and should be handled specially
 */
const LARGE_DATA_FIELDS: (keyof Shape)[] = [
  '_pdfBase64',
  'imageDataUrl',
];

/**
 * Maximum size for inline data (5MB - PDF, 이미지 등 대용량 데이터 허용)
 */
const MAX_INLINE_DATA_SIZE = 5 * 1024 * 1024;

/**
 * Serialize a Shape to ShapeData for network transmission
 * Removes client-only fields and handles large data
 */
export function serializeShape(shape: Shape): ShapeData {
  const serialized: ShapeData = {
    id: shape.id,
    type: shape.type,
    x: shape.x,
    y: shape.y,
  };

  // Copy all serializable fields
  for (const [key, value] of Object.entries(shape)) {
    if (EXCLUDED_FIELDS.includes(key as keyof Shape)) {
      continue;
    }

    // Handle large data fields - truncate or skip if too large
    if (LARGE_DATA_FIELDS.includes(key as keyof Shape)) {
      if (typeof value === 'string' && value.length > MAX_INLINE_DATA_SIZE) {
        // Skip large data for now - could be handled via separate upload
        continue;
      }
    }

    if (value !== undefined) {
      serialized[key] = value;
    }
  }

  return serialized;
}

/**
 * Deserialize ShapeData back to Shape
 */
export function deserializeShape(data: ShapeData): Shape {
  return data as Shape;
}

/**
 * Serialize multiple shapes
 */
export function serializeShapes(shapes: Shape[]): ShapeData[] {
  return shapes.map(serializeShape);
}

/**
 * Deserialize multiple shapes
 */
export function deserializeShapes(data: ShapeData[]): Shape[] {
  return data.map(deserializeShape);
}

/**
 * Calculate the diff between two shape arrays
 */
export function calculateDiff(
  previousShapes: Shape[],
  currentShapes: Shape[]
): ShapeDiff {
  const previousMap = new Map(previousShapes.map((s) => [s.id, s]));
  const currentMap = new Map(currentShapes.map((s) => [s.id, s]));

  const added: Shape[] = [];
  const updated: Shape[] = [];
  const deleted: string[] = [];

  // Find added and updated shapes
  currentMap.forEach((shape, id) => {
    const previousShape = previousMap.get(id);

    if (!previousShape) {
      added.push(shape);
    } else if (!areShapesEqual(previousShape, shape)) {
      updated.push(shape);
    }
  });

  // Find deleted shapes
  previousMap.forEach((_, id) => {
    if (!currentMap.has(id)) {
      deleted.push(id);
    }
  });

  return {
    added,
    updated,
    deleted,
    hasChanges: added.length > 0 || updated.length > 0 || deleted.length > 0,
  };
}

/**
 * Compare two shapes for equality (deep comparison of relevant fields)
 */
export function areShapesEqual(a: Shape, b: Shape): boolean {
  // Quick check - same reference
  if (a === b) return true;

  // Check all relevant fields
  for (const key of Object.keys(a) as (keyof Shape)[]) {
    // Skip excluded fields
    if (EXCLUDED_FIELDS.includes(key)) continue;

    const valueA = a[key];
    const valueB = b[key];

    // Handle undefined vs missing
    if (valueA === undefined && valueB === undefined) continue;
    if (valueA === undefined || valueB === undefined) return false;

    // Deep compare arrays
    if (Array.isArray(valueA) && Array.isArray(valueB)) {
      if (!areArraysEqual(valueA, valueB)) return false;
      continue;
    }

    // Compare primitives
    if (valueA !== valueB) return false;
  }

  // Check keys in b that might not be in a
  for (const key of Object.keys(b) as (keyof Shape)[]) {
    if (EXCLUDED_FIELDS.includes(key)) continue;
    if (!(key in a) && b[key] !== undefined) return false;
  }

  return true;
}

/**
 * Compare two arrays for equality (deep comparison for objects)
 */
function areArraysEqual(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    const itemA = a[i];
    const itemB = b[i];

    // Reference equality check first
    if (itemA === itemB) continue;

    // If both are objects, do deep comparison
    if (
      typeof itemA === 'object' &&
      typeof itemB === 'object' &&
      itemA !== null &&
      itemB !== null
    ) {
      // Use JSON stringify for deep comparison (works for annotations)
      if (JSON.stringify(itemA) !== JSON.stringify(itemB)) {
        return false;
      }
    } else if (itemA !== itemB) {
      return false;
    }
  }

  return true;
}

/**
 * Detect changes between previous and current shapes
 * Returns a list of individual changes
 */
export function detectChanges(
  previousShapes: Shape[],
  currentShapes: Shape[]
): ShapeChange[] {
  const changes: ShapeChange[] = [];
  const diff = calculateDiff(previousShapes, currentShapes);

  // Add changes
  for (const shape of diff.added) {
    changes.push({
      type: 'add',
      shapeId: shape.id,
      shape,
    });
  }

  // Update changes
  for (const shape of diff.updated) {
    const previousShape = previousShapes.find((s) => s.id === shape.id);
    changes.push({
      type: 'update',
      shapeId: shape.id,
      shape,
      previousShape,
    });
  }

  // Delete changes
  for (const shapeId of diff.deleted) {
    const previousShape = previousShapes.find((s) => s.id === shapeId);
    changes.push({
      type: 'delete',
      shapeId,
      previousShape,
    });
  }

  return changes;
}

/**
 * Apply remote changes to local shapes
 * Returns the new shapes array
 */
export function applyRemoteChanges(
  localShapes: Shape[],
  action: UpdateAction,
  data: {
    shapes?: ShapeData[];
    shapeId?: string;
    shapeIds?: string[];
  }
): Shape[] {
  const { shapes, shapeId, shapeIds } = data;

  switch (action) {
    case 'add': {
      if (!shapes || shapes.length === 0) return localShapes;

      const newShapes = deserializeShapes(shapes);
      const existingIds = new Set(localShapes.map((s) => s.id));

      // Only add shapes that don't already exist
      const toAdd = newShapes.filter((s) => !existingIds.has(s.id));

      return [...localShapes, ...toAdd];
    }

    case 'update': {
      if (!shapes || shapes.length === 0) return localShapes;

      const updateMap = new Map(shapes.map((s) => [s.id, s]));

      return localShapes.map((shape) => {
        const update = updateMap.get(shape.id);
        if (update) {
          return { ...shape, ...deserializeShape(update) };
        }
        return shape;
      });
    }

    case 'delete': {
      if (shapeId) {
        return localShapes.filter((s) => s.id !== shapeId);
      }

      if (shapeIds && shapeIds.length > 0) {
        const idsToDelete = new Set(shapeIds);
        return localShapes.filter((s) => !idsToDelete.has(s.id));
      }

      return localShapes;
    }

    case 'bulk': {
      // Replace entire shapes array
      if (shapes) {
        return deserializeShapes(shapes);
      }
      return localShapes;
    }

    default:
      return localShapes;
  }
}

/**
 * Create an update payload from a diff
 */
export function createUpdatePayload(
  diff: ShapeDiff
): { action: UpdateAction; shapes?: ShapeData[]; shapeIds?: string[] } | null {
  // If there are deletions only
  if (diff.deleted.length > 0 && diff.added.length === 0 && diff.updated.length === 0) {
    return {
      action: 'delete',
      shapeIds: diff.deleted,
    };
  }

  // If there are only additions
  if (diff.added.length > 0 && diff.updated.length === 0 && diff.deleted.length === 0) {
    return {
      action: 'add',
      shapes: serializeShapes(diff.added),
    };
  }

  // If there are only updates
  if (diff.updated.length > 0 && diff.added.length === 0 && diff.deleted.length === 0) {
    return {
      action: 'update',
      shapes: serializeShapes(diff.updated),
    };
  }

  // Mixed changes - use bulk update
  if (diff.hasChanges) {
    return {
      action: 'bulk',
      shapes: serializeShapes([...diff.added, ...diff.updated]),
      shapeIds: diff.deleted,
    };
  }

  return null;
}

/**
 * Merge remote shapes into local shapes
 * Used when joining a session to sync initial state
 */
export function mergeShapes(
  localShapes: Shape[],
  remoteShapes: ShapeData[],
  preferRemote: boolean = true
): Shape[] {
  if (preferRemote) {
    // Remote shapes take precedence
    return deserializeShapes(remoteShapes);
  }

  // Merge - keep local shapes that don't exist remotely
  const remoteIds = new Set(remoteShapes.map((s) => s.id));
  const localOnlyShapes = localShapes.filter((s) => !remoteIds.has(s.id));

  return [...deserializeShapes(remoteShapes), ...localOnlyShapes];
}

/**
 * Throttle function for limiting update frequency
 */
export function createThrottle<T extends (...args: any[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    lastArgs = args;

    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    } else if (!timeout) {
      timeout = setTimeout(() => {
        lastCall = Date.now();
        timeout = null;
        if (lastArgs) {
          fn(...lastArgs);
        }
      }, delay - (now - lastCall));
    }
  };
}

/**
 * Debounce function for batching rapid updates
 */
export function createDebounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      timeout = null;
      fn(...args);
    }, delay);
  };
}

/**
 * Generate a unique operation ID for conflict resolution
 */
export function generateOperationId(): string {
  return `op_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}
